/**
 * Catálogo de skins del CMDB (eje FORMA).
 *
 * El CMDB tiene dos ejes de aspecto independientes:
 *
 *   1. `data-theme` → COLOR. Son las 12 paletas (`theme/palettes.js`): fondo,
 *      superficies y acento. Cambiar de paleta cambia el color, no la forma.
 *
 *   2. `data-skin`  → FORMA. Son estos skins: radios, sombras, profundidad,
 *      densidad, barra lateral, tarjetas, tablas y controles. Lo implementa
 *      `styles/skins.css` y se carga el último, así que manda sobre el resto.
 *
 * Un skin NUNCA fija colores de acento: usa `var(--accent)`. Por eso las 12
 * paletas combinan con los 5 skins sin que haya que mantener 60 combinaciones.
 *
 * `base` es el modo claro/oscuro que el skin impone por su cuenta:
 *
 *   - 'light' → el skin define sus propias superficies claras (da igual la paleta)
 *   - 'dark'  → ídem en oscuro
 *   - null    → respeta el modo que pida la paleta (comportamiento clásico)
 *
 * Cuando `base` no es null, `applyAppearance` añade el atributo
 * `data-skin-base`, que es el interruptor de la capa de skin en `skins.css`.
 * El skin `classic` no lo lleva: así el aspecto de siempre queda intacto.
 */

export const SKINS = [
  {
    id: 'classic',
    name: 'Classic',
    tagline: 'El aspecto actual',
    description:
      'Sin cambios: el CMDB tal y como está hoy. Es el valor por defecto, de modo que este sistema no altera nada si nadie elige otra cosa.',
    base: null,
    preview: {
      page: '#07090f',
      card: '#0d0f1a',
      border: 'rgba(255,255,255,.09)',
      text: '#e2e8f0',
      muted: 'rgba(255,255,255,.34)',
      radius: 5,
      blur: false,
      glow: false,
    },
  },
  {
    id: 'lumina',
    name: 'Lumina',
    tagline: 'Claro y suave',
    description:
      'Fondo con degradado pastel, tarjetas de cristal con sombras difusas y esquinas muy redondeadas. Es el más cercano a las referencias que enviaste.',
    base: 'light',
    preview: {
      page: 'linear-gradient(160deg,#fdf2f8,#f5f1fd 48%,#eef4fe)',
      card: 'rgba(255,255,255,.88)',
      border: 'rgba(255,255,255,.95)',
      text: '#2a2440',
      muted: 'rgba(42,36,64,.42)',
      radius: 14,
      blur: true,
      glow: false,
    },
  },
  {
    id: 'lumina-dark',
    name: 'Lumina Dark',
    tagline: 'Oscuro suave',
    description:
      'La misma forma que Lumina sobre un fondo oscuro con un degradado tenue del acento. Elegante sin cansar la vista en jornadas largas.',
    base: 'dark',
    preview: {
      page: 'linear-gradient(160deg,#171326,#151429 55%,#111726)',
      card: 'rgba(255,255,255,.05)',
      border: 'rgba(255,255,255,.1)',
      text: '#eae7f6',
      muted: 'rgba(234,231,246,.38)',
      radius: 14,
      blur: true,
      glow: false,
    },
  },
  {
    id: 'onyx',
    name: 'Onyx',
    tagline: 'Oscuro de alto contraste',
    description:
      'Dos niveles de superficie, cabeceras con degradado del acento y resplandor en los elementos activos. Estilo SaaS moderno, muy rotundo.',
    base: 'dark',
    preview: {
      page: '#08090d',
      card: '#12151d',
      border: 'rgba(255,255,255,.09)',
      text: '#f2f4f8',
      muted: 'rgba(242,244,248,.34)',
      radius: 11,
      blur: false,
      glow: true,
    },
  },
  {
    id: 'nordic',
    name: 'Nordic',
    tagline: 'Plano y minimalista',
    description:
      'Sin sombras ni degradados: líneas de 1px, mucho aire y tipografía grande. Es el que mejor aguanta los listados largos de infraestructura.',
    base: 'light',
    preview: {
      page: '#fafafa',
      card: '#ffffff',
      border: '#e7e7ec',
      text: '#17171c',
      muted: 'rgba(23,23,28,.42)',
      radius: 9,
      blur: false,
      glow: false,
    },
  },
];

export const DEFAULT_SKIN = 'classic';

/** Skins que NO son el de siempre: son los que activan la capa de forma. */
export const CUSTOM_SKIN_IDS = SKINS.filter((s) => s.base).map((s) => s.id);

const BY_ID = new Map(SKINS.map((s) => [s.id, s]));

export function getSkin(id) {
  return BY_ID.get(id) || BY_ID.get(DEFAULT_SKIN);
}

export function isValidSkin(id) {
  return BY_ID.has(id);
}
